import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { Poster, Figure } from '../types';
import Editor from '../components/Editor';
import FigureEditor from '../components/FigureEditor';
import Preview from '../components/Preview';
import ShareDialog from '../components/ShareDialog';
import Button from '../components/ui/Button';
import FloatingActionButton from '../components/ai/FloatingActionButton';
import AISidebar from '../components/ai/AISidebar';

export default function EditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [poster, setPoster] = useState<Poster | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [activeTab, setActiveTab] = useState<'edit' | 'preview'>('edit');
  const [isAISidebarOpen, setIsAISidebarOpen] = useState(false);

  useEffect(() => {
    if (id) {
      loadPoster(id);
    }
  }, [id]);

  const loadPoster = async (posterId: string) => {
    try {
      setLoading(true);
      const data = await api.getPoster(posterId);
      setPoster(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load poster');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (updates: Partial<Poster>) => {
    if (!id || !poster) return;

    try {
      const updated = await api.updatePoster(id, updates);
      setPoster(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update poster');
    }
  };

  const handleFigureUpdate = async (figure: Figure) => {
    if (!id || !poster) return;

    try {
      if (figure.id) {
        await api.updateFigure(id, figure.id, figure);
      } else {
        await api.createFigure(id, figure);
      }
      await loadPoster(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update figure');
    }
  };

  const handleFigureDelete = async (figureId: string) => {
    if (!id) return;

    try {
      await api.deleteFigure(id, figureId);
      await loadPoster(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete figure');
    }
  };

  const handleAddFigure = async () => {
    if (!id) return;

    const newFigure: Partial<Figure> = {
      description: 'New Figure',
      image: '',
    };

    try {
      await api.createFigure(id, newFigure);
      await loadPoster(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add figure');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-lg text-neutral-600">Loading poster...</p>
      </div>
    );
  }

  if (error || !poster) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen px-4">
        <p className="text-lg text-red-600 mb-4">{error || 'Poster not found'}</p>
        <Button onClick={() => navigate('/')}>
          Go Home
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="bg-white border-b border-neutral-200 px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between 
                        items-start sm:items-center gap-4">
          <h1 className="text-xl sm:text-2xl font-display font-bold text-neutral-900">
            Edit Poster
          </h1>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => setActiveTab(activeTab === 'edit' ? 'preview' : 'edit')}
            >
              {activeTab === 'edit' ? 'Preview' : 'Edit'}
            </Button>
            <Button
              variant="success"
              onClick={() => setShowShareDialog(true)}
            >
              Share
            </Button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 
                        mx-4 mt-4 rounded-lg">
          {error}
        </div>
      )}

      {activeTab === 'edit' ? (
        <div>
          <Editor
            title={poster.title}
            authors={poster.authors}
            affiliations={poster.affiliations}
            summary={poster.summary}
            keyTakeaways={poster.keyTakeaways}
            sections={poster.sections}
            onUpdate={handleUpdate}
          />

          <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row justify-between items-start 
                            sm:items-center mb-6 gap-4">
              <h2 className="text-xl sm:text-2xl font-display font-bold text-neutral-900">
                Figures
              </h2>
              <Button variant="success" onClick={handleAddFigure}>
                + Add Figure
              </Button>
            </div>

            <div className="space-y-4">
              {poster.figures.map((figure, index) => (
                <FigureEditor
                  key={figure.id || index}
                  figure={figure}
                  originalImageUrl={poster.originalImageUrl}
                  onUpdate={handleFigureUpdate}
                  onDelete={figure.id ? () => handleFigureDelete(figure.id!) : undefined}
                />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="px-4 sm:px-6 lg:px-8 py-8 md:py-12">
          <Preview poster={poster} />
        </div>
      )}

      {showShareDialog && (
        <ShareDialog posterId={id!} onClose={() => setShowShareDialog(false)} />
      )}

      {poster && (
        <>
          <FloatingActionButton
            onClick={() => setIsAISidebarOpen(true)}
            isOpen={isAISidebarOpen}
          />
          <AISidebar
            poster={poster}
            isOpen={isAISidebarOpen}
            onClose={() => setIsAISidebarOpen(false)}
          />
        </>
      )}
    </div>
  );
}
